import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, ReactNode } from "react";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import IOnBulkAddFavoritesDTO from "../models/DTO/IOnBulkAddFavoritesDTO";
import { IUiContextModel } from "./UiContext";
import FavoritesService from "../services/FavoritesService";

/**
 * Session-level favorites state for the ecommerce portal (OR-1488).
 *
 * The favorited-guid set is loaded once per session from GET /v1/favorites/ids and then kept in
 * sync locally: toggles apply optimistically and roll back if the server rejects them. `version`
 * bumps on every successful change so list surfaces (home section, quick order tab) can refetch.
 *
 * Favorites belong to the customer account (vendorGuid header), so the context is only enabled for
 * portal customers — supplier users (supplier-side storefront, admin card preview) never see hearts.
 */
export interface IFavoritesContext {
    /** True for signed-in ecommerce portal customers; everything else is a no-op with no UI. */
    enabled: boolean;
    /** True once the id set has been fetched for the current account. */
    loaded: boolean;
    favoriteGuids: Set<string>;
    isFavorite: (catalogItemGuid: string | undefined | null) => boolean;
    /** Optimistic add/remove; ignored while a request for the same guid is in flight. */
    toggleFavorite: (catalogItemGuid: string) => Promise<void>;
    /** Bulk add (idempotent); merges the accepted guids into the set. */
    addFavorites: (catalogItemGuids: string[]) => Promise<IOnBulkAddFavoritesDTO>;
    /** Increments on every successful change to the list. */
    version: number;
}

const DISABLED_FAVORITES: IFavoritesContext = {
    enabled: false,
    loaded: false,
    favoriteGuids: new Set<string>(),
    isFavorite: () => false,
    toggleFavorite: async () => {},
    addFavorites: async () => ({ added: 0, already_favorited: 0, invalid: [] }),
    version: 0,
};

// A disabled default (rather than undefined + throw) keeps components that render the toggle
// usable outside the provider, e.g. existing tests of the card/list/quick-order views.
const FavoritesContext = createContext<IFavoritesContext>(DISABLED_FAVORITES);

export interface FavoritesProviderProps {
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    vendorModel?: IVendorModel;
    uiContext?: IUiContextModel;
    children: ReactNode;
}

/** Same predicate as App.tsx's isEcommPortalUser, plus the auth material the REST calls need. */
export const isFavoritesEnabledFor = (userModel?: IUserUI, supplierModel?: ISupplierModel, vendorModel?: IVendorModel): boolean => {
    return (
        !!userModel?.guid &&
        !!userModel?.token &&
        !userModel.isSupplierUser &&
        !!supplierModel?.guid &&
        !!vendorModel?.guid &&
        !!vendorModel?.is_ecomm_user
    );
};

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ userModel, supplierModel, vendorModel, uiContext, children }) => {
    const enabled = isFavoritesEnabledFor(userModel, supplierModel, vendorModel);

    const [favoriteGuids, setFavoriteGuids] = useState<Set<string>>(() => new Set());
    const [loaded, setLoaded] = useState(false);
    const [version, setVersion] = useState(0);

    // Latest models/context for the async callbacks without re-creating them on every render.
    // Synced after commit (a render may be discarded) and before the passive effects that read it.
    const modelsRef = useRef({ userModel, supplierModel, vendorModel, uiContext });
    useLayoutEffect(() => {
        modelsRef.current = { userModel, supplierModel, vendorModel, uiContext };
    }, [userModel, supplierModel, vendorModel, uiContext]);

    // Guids with a request in flight; a second tap while pending is ignored rather than raced.
    const inFlightRef = useRef<Set<string>>(new Set());
    // Identity of the account whose list is loaded; bumps on every account switch so a stale ids
    // response, or a mutation that completes after the switch, cannot touch the new account's set.
    const loadIdRef = useRef(0);

    const userGuid = userModel?.guid;
    const supplierGuid = supplierModel?.guid;
    const vendorGuid = vendorModel?.guid;

    useEffect(() => {
        const loadId = ++loadIdRef.current;

        setFavoriteGuids(new Set());
        setLoaded(false);
        inFlightRef.current = new Set();

        if (!enabled) {
            return;
        }

        const { userModel: user, supplierModel: supplier, vendorModel: vendor } = modelsRef.current;

        FavoritesService.getFavoriteIds(user, supplier, vendor)
            .then((result) => {
                if (loadId !== loadIdRef.current) {
                    return;
                }

                setFavoriteGuids(new Set(result.catalog_item_guids || []));
                setLoaded(true);
            })
            .catch((error) => {
                // Fail quiet: the catalog stays usable, hearts simply render as unfavorited.
                console.warn("Failed to load favorites", error);
            });
    }, [enabled, userGuid, supplierGuid, vendorGuid]);

    const isFavorite = useCallback(
        (catalogItemGuid: string | undefined | null): boolean => {
            return !!catalogItemGuid && favoriteGuids.has(catalogItemGuid);
        },
        [favoriteGuids]
    );

    const setHasFavorite = (catalogItemGuid: string, favorited: boolean) => {
        setFavoriteGuids((prev) => {
            if (prev.has(catalogItemGuid) === favorited) {
                return prev;
            }

            const next = new Set(prev);

            if (favorited) {
                next.add(catalogItemGuid);
            } else {
                next.delete(catalogItemGuid);
            }

            return next;
        });
    };

    const toggleFavorite = useCallback(
        async (catalogItemGuid: string): Promise<void> => {
            if (!enabled || !catalogItemGuid || inFlightRef.current.has(catalogItemGuid)) {
                return;
            }

            const { userModel: user, supplierModel: supplier, vendorModel: vendor, uiContext: ui } = modelsRef.current;
            const wasFavorite = favoriteGuids.has(catalogItemGuid);
            const accountLoadId = loadIdRef.current;

            inFlightRef.current.add(catalogItemGuid);
            setHasFavorite(catalogItemGuid, !wasFavorite);

            try {
                if (wasFavorite) {
                    await FavoritesService.removeFavorite(catalogItemGuid, user, supplier, vendor);
                } else {
                    await FavoritesService.addFavorite(catalogItemGuid, user, supplier, vendor);
                }

                if (accountLoadId !== loadIdRef.current) {
                    return;
                }

                setVersion((v) => v + 1);
            } catch {
                if (accountLoadId !== loadIdRef.current) {
                    // The account changed while this was in flight; its set was reset, nothing to roll back.
                    return;
                }

                setHasFavorite(catalogItemGuid, wasFavorite);

                ui?.notifyFn?.({
                    eid: "",
                    title: "error",
                    message: "favorite_update_failed",
                });
            } finally {
                inFlightRef.current.delete(catalogItemGuid);
            }
        },
        [enabled, favoriteGuids]
    );

    const addFavorites = useCallback(
        async (catalogItemGuids: string[]): Promise<IOnBulkAddFavoritesDTO> => {
            const guids = Array.from(new Set(catalogItemGuids.filter((guid) => !!guid)));

            if (!enabled || !guids.length) {
                return { added: 0, already_favorited: 0, invalid: [] };
            }

            const { userModel: user, supplierModel: supplier, vendorModel: vendor } = modelsRef.current;
            const accountLoadId = loadIdRef.current;

            const result = await FavoritesService.bulkAddFavorites(guids, user, supplier, vendor);

            if (accountLoadId !== loadIdRef.current) {
                // Account changed mid-request: the server applied it to the old account; don't merge here.
                return result;
            }

            const invalid = new Set(result.invalid || []);

            setFavoriteGuids((prev) => {
                const next = new Set(prev);

                for (const guid of guids) {
                    if (!invalid.has(guid)) {
                        next.add(guid);
                    }
                }

                return next;
            });
            setVersion((v) => v + 1);

            return result;
        },
        [enabled]
    );

    const value = useMemo<IFavoritesContext>(
        () => ({ enabled, loaded, favoriteGuids, isFavorite, toggleFavorite, addFavorites, version }),
        [enabled, loaded, favoriteGuids, isFavorite, toggleFavorite, addFavorites, version]
    );

    return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};

export const useFavorites = (): IFavoritesContext => {
    return useContext(FavoritesContext);
};

export default FavoritesContext;
